<template>
    <BCard no-body class="compact-regulation-review-card mb-75">
        <div
            class="compact-regulation-review-card__header"
            role="button"
            tabindex="0"
            :aria-expanded="expanded ? 'true' : 'false'"
            @click="$emit('toggle')"
            @keydown.enter.prevent="$emit('toggle')"
            @keydown.space.prevent="$emit('toggle')"
        >
            <span class="compact-regulation-review-card__number">{{ number }}</span>
            <div class="compact-regulation-review-card__identity">
                <div class="compact-regulation-review-card__name-row">
                    <strong class="compact-regulation-review-card__name">
                        {{ regulation.name || translate('Component(ResearchReviewProcess):unnamed-legislation', 'Unnamed legislation') }}
                    </strong>
                    <BBadge v-if="regulation.status" variant="light-secondary">
                        {{ statusLabel(regulation.status) }}
                    </BBadge>
                </div>
                <small class="text-less-muted">{{ summaryText }}</small>
            </div>
            <div class="compact-regulation-review-card__actions">
                <ReviewNeedsAttentionBadge :checks="evaluation.checks" />
                <BButton
                    type="button"
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon"
                    :title="translate('Component(Button):remove', 'Remove')"
                    @click.stop="$emit('remove')"
                >
                    <FeatherIcon icon="Trash2Icon" size="16" />
                </BButton>
                <FeatherIcon
                    :icon="expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="16"
                    class="compact-regulation-review-card__chevron"
                />
            </div>
        </div>

        <div v-show="expanded" class="compact-regulation-review-card__body">
            <BRow>
                <BCol cols="12" md="6">
                    <BFormGroup :label="translate('Component(ResearchReviewProcess):legislation-name', 'Legislation name')">
                        <BFormInput
                            :value="regulation.name"
                            @input="updateRegulation('name', $event)"
                        />
                    </BFormGroup>
                </BCol>
                <BCol cols="12" md="6">
                    <BFormGroup :label="translate('Component(ResearchReviewProcess):normalized-legislation', 'Normalized legislation')">
                        <VSelect
                            :value="selectedOption(legislationKeyOptions, regulation.key)"
                            :options="legislationKeyOptions"
                            label="label"
                            :clearable="false"
                            :append-to-body="true"
                            :selectable="isLegislationOptionSelectable"
                            :placeholder="translate('Component(ResearchReviewProcess):select-legislation', 'Select legislation')"
                            @input="updateRegulation('key', $event ? $event.id : null)"
                        />
                    </BFormGroup>
                </BCol>
                <BCol cols="12" md="6">
                    <BFormGroup :label="translate('Component(ComplianceOverview):column-status', 'Coverage status')" class="mb-md-0">
                        <VSelect
                            :value="selectedOption(statusOptions, regulation.status)"
                            :options="statusOptions"
                            label="label"
                            :clearable="false"
                            :append-to-body="true"
                            :placeholder="translate('Component(ResearchReviewProcess):select-coverage-status', 'Select coverage status')"
                            @input="updateRegulation('status', $event ? $event.id : null)"
                        />
                    </BFormGroup>
                </BCol>
                <BCol cols="12" md="6">
                    <BFormGroup :label="translate('Component(ResearchReviewProcess):evidence', 'Evidence')" class="mb-0">
                        <BFormTextarea
                            :value="regulation.evidence"
                            rows="2"
                            max-rows="5"
                            :placeholder="translate('Component(ResearchReviewProcess):evidence-placeholder', 'Source text or page reference')"
                            @input="updateRegulation('evidence', $event || null)"
                        />
                    </BFormGroup>
                </BCol>
            </BRow>

            <div class="compact-regulation-review-card__sections">
                <section class="compact-regulation-review-card__section">
                    <div
                        class="compact-regulation-review-card__section-header"
                        role="button"
                        tabindex="0"
                        @click="toggleSubsection('claims')"
                        @keydown.enter.prevent="toggleSubsection('claims')"
                        @keydown.space.prevent="toggleSubsection('claims')"
                    >
                        <div>
                            <strong>{{ translate('Component(ResearchReviewProcess):claims', 'Claims') }}</strong>
                            <BBadge variant="light-secondary" class="ml-50">{{ claims.length }}</BBadge>
                        </div>
                        <div class="d-flex align-items-center">
                            <BButton
                                v-if="usesStructuredClaimsEditor"
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                @click.stop="addClaim"
                            >
                                <FeatherIcon icon="PlusIcon" size="14" class="mr-25" />
                                {{ translate('Component(ResearchReviewProcess):add-claim', 'Add claim') }}
                            </BButton>
                            <FeatherIcon
                                :icon="isSubsectionExpanded('claims') ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                                size="15"
                                class="ml-75 text-muted"
                            />
                        </div>
                    </div>

                    <div v-show="isSubsectionExpanded('claims')" class="compact-regulation-review-card__section-body">
                        <p class="review-section-description text-less-muted mb-75">
                            {{ translate(
                                'Component(ResearchReviewProcess):claims-description',
                                'Confirm the statements extracted for this legislation.',
                            ) }}
                        </p>
                        <component
                            :is="claimsEditorComponent"
                            :claims="claims"
                            @input="updateClaims"
                        />
                    </div>
                </section>

                <section v-if="relatedSubstances.length" class="compact-regulation-review-card__section">
                    <div
                        class="compact-regulation-review-card__section-header"
                        role="button"
                        tabindex="0"
                        @click="toggleSubsection('substances')"
                        @keydown.enter.prevent="toggleSubsection('substances')"
                        @keydown.space.prevent="toggleSubsection('substances')"
                    >
                        <div>
                            <strong>{{ translate('Component(ResearchReviewProcess):related-substances', 'Related substances') }}</strong>
                            <BBadge variant="light-secondary" class="ml-50">{{ relatedSubstances.length }}</BBadge>
                        </div>
                        <FeatherIcon
                            :icon="isSubsectionExpanded('substances') ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                            size="15"
                            class="text-muted"
                        />
                    </div>
                    <div v-show="isSubsectionExpanded('substances')" class="compact-regulation-review-card__section-body">
                        <p class="review-section-description text-less-muted mb-75">
                            {{ translate(
                                'Component(ResearchReviewProcess):related-substances-description',
                                'Chemity substances associated with this regulation through the extracted document data.',
                            ) }}
                        </p>
                        <RegulationRelatedSubstances
                            :items="relatedSubstances"
                            :show-header="false"
                        />
                    </div>
                </section>

                <section class="compact-regulation-review-card__section">
                    <div
                        class="compact-regulation-review-card__section-header"
                        role="button"
                        tabindex="0"
                        @click="toggleSubsection('exemptions')"
                        @keydown.enter.prevent="toggleSubsection('exemptions')"
                        @keydown.space.prevent="toggleSubsection('exemptions')"
                    >
                        <div>
                            <strong>{{ translate('Component(ResearchReviewProcess):exemptions', 'Exemptions') }}</strong>
                            <BBadge variant="light-secondary" class="ml-50">{{ exemptions.length }}</BBadge>
                        </div>
                        <div class="d-flex align-items-center">
                            <BButton type="button" size="sm" variant="outline-primary" @click.stop="$emit('add-exemption')">
                                <FeatherIcon icon="PlusIcon" size="14" class="mr-25" />
                                {{ translate('Component(ResearchReviewProcess):add-exemption', 'Add exemption') }}
                            </BButton>
                            <FeatherIcon
                                :icon="isSubsectionExpanded('exemptions') ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                                size="15"
                                class="ml-75 text-muted"
                            />
                        </div>
                    </div>
                    <div v-show="isSubsectionExpanded('exemptions')" class="compact-regulation-review-card__section-body">
                        <p class="review-section-description text-less-muted mb-75">
                            {{ translate(
                                'Component(ResearchReviewProcess):exemptions-description',
                                'Review exemptions associated with this legislation.',
                            ) }}
                        </p>
                        <p v-if="!exemptions.length" class="text-less-muted mb-0">
                            {{ translate('Component(ResearchReviewProcess):no-exemptions', 'No exemptions were extracted for this legislation.') }}
                        </p>
                        <div v-else class="compact-regulation-review-card__exemptions">
                            <RegulationExemptionRow
                                v-for="exemption in exemptions"
                                :key="`exemption-${exemption.sourceIndex}`"
                                :value="exemption.value"
                                @input="$emit('update-exemption', { sourceIndex: exemption.sourceIndex, value: $event })"
                                @remove="$emit('remove-exemption', exemption.sourceIndex)"
                            />
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </BCard>
</template>

<script>
import {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
} from 'bootstrap-vue'
import VSelect from 'vue-select'
import ReviewNeedsAttentionBadge from '../components/ReviewNeedsAttentionBadge.vue'
import RegulationExemptionRow from './RegulationExemptionRow.vue'
import RegulationRelatedSubstances from './RegulationRelatedSubstances.vue'
import regulationReviewCardMixin from './regulation-review-card-mixin'

export default {
    name: 'CompactRegulationReviewCard',
    components: {
        BBadge,
        BButton,
        BCard,
        BCol,
        BFormGroup,
        BFormInput,
        BFormTextarea,
        BRow,
        RegulationExemptionRow,
        RegulationRelatedSubstances,
        ReviewNeedsAttentionBadge,
        VSelect,
    },
    mixins: [regulationReviewCardMixin],
    data() {
        return {
            expandedSubsection: null,
        }
    },
    methods: {
        isSubsectionExpanded(subsection) {
            return this.expandedSubsection === subsection
        },
        toggleSubsection(subsection) {
            this.expandedSubsection = this.isSubsectionExpanded(subsection) ? null : subsection
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/variables/variables';

.compact-regulation-review-card {
    overflow: visible;
    border-color: #dedbe5;
    box-shadow: 0 2px 8px rgba(34, 41, 47, .04);
}

.compact-regulation-review-card__header {
    display: grid;
    grid-template-columns: 2rem minmax(0, 1fr) auto;
    gap: .75rem;
    align-items: center;
    padding: .85rem 1rem;
    cursor: pointer;

    &:focus {
        outline: 2px solid rgba($chemity-accent, .35);
        outline-offset: -2px;
    }
}

.compact-regulation-review-card__number {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    color: $chemity-accent;
    font-weight: 600;
    background: rgba($chemity-accent, .1);
    border-radius: 50%;
}

.compact-regulation-review-card__identity,
.compact-regulation-review-card__name {
    min-width: 0;
}

.compact-regulation-review-card__name-row,
.compact-regulation-review-card__actions {
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
    align-items: center;
}

.compact-regulation-review-card__actions {
    flex-wrap: nowrap;
}

.compact-regulation-review-card__chevron {
    color: #82868b;
}

.compact-regulation-review-card__body {
    padding: 1rem;
    border-top: 1px solid #ebe9f1;
}

.compact-regulation-review-card__sections {
    margin-top: 1rem;
    border-top: 1px solid #ebe9f1;
}

.compact-regulation-review-card__section {
    border-bottom: 1px solid #ebe9f1;
}

.compact-regulation-review-card__section-header {
    display: flex;
    min-height: 3.25rem;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: .65rem .25rem;
    cursor: pointer;

    &:focus {
        outline: 2px solid rgba($chemity-accent, .25);
        outline-offset: -2px;
    }
}

.compact-regulation-review-card__section-body {
    padding: .25rem .25rem 1rem;
}

.compact-regulation-review-card__exemptions {
    display: grid;
    gap: .75rem;
}

@media (max-width: 767.98px) {
    .compact-regulation-review-card__header {
        grid-template-columns: 2rem minmax(0, 1fr);
    }

    .compact-regulation-review-card__actions {
        grid-column: 2;
        justify-content: flex-end;
    }

    .compact-regulation-review-card__section-header {
        align-items: flex-start;
    }
}
</style>
